import React from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import styles from "./treatmentProtocolDetails.module.css";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../../services/selectors/utils.selectors";
import { useGetAllMetaTablesQuery } from "../../../services/api/utils.api";
import { AppointmentDevice, DeviceConfig, NfElectrode } from "../../../services/types/device.types";
import {
  DEVICE_CATEGORY_DISPLAY,
  LORETA_MODE_DISPLAY,
  NF_WAVE_BANDS,
  NF_WAVE_MODES,
  getActiveNfWaves,
  isStimulationCategory,
  supportsFrequency,
} from "../../../utils/deviceConfiguration";

/** Read-only label/value pair. Shared by every details view. */
export function Item({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className={styles.item}>
      <span className={styles.itemLabel}>{label}</span>
      <span className={styles.itemValue}>{value}</span>
    </div>
  );
}

/**
 * The read-only body of a device configuration: settings, then whichever montage the modality
 * uses. Shared by the protocol details view and the appointment details view, so a technician sees
 * exactly the same breakdown whether they open it from the protocol or from the calendar.
 */
export function DeviceConfigSections({ config }: { config?: DeviceConfig | null }) {
  // Hooks run before the early return: the frequency is a lookup id, and its label lives in the
  // same meta the treatment plan reads.
  const admin = useAppSelector(adminSelector);
  useGetAllMetaTablesQuery(admin.companyId, { skip: !admin.companyId });
  const meta = useAppSelector(appointmentsProgramsDataSelector);

  const frequencyLabel = config?.appointmentsSequenceId
    ? meta.AppointmentsSequence.map((s) => ({
        value: Number(s.value ?? s.Id),
        label: String(s.label ?? s.CountInPeriod),
      })).find((o) => o.value === config.appointmentsSequenceId)?.label
    : undefined;

  if (!config?.category) return null;

  const category = config.category;

  return (
    <>
      <section className={styles.section}>
        <div className={styles.sectionTitle}>{t("DEVICE_CONFIGURATION")}</div>
        <div className={styles.grid}>
          <Item label={t("DEVICE_TYPE")} value={DEVICE_CATEGORY_DISPLAY[category] ?? category} />
          <Item
            label={t("DEVICE_DURATION_MINUTES")}
            value={config.durationMinutes ? `${config.durationMinutes} ${t("MINUTES_SHORT")}` : "—"}
          />
          {/* The cadence, from the same catalogue the treatment plan uses. Older protocols that
              still carry the free number show it instead, so nothing reads as empty. */}
          <Item
            label={t("APPOINTMENT_FREQUENCY")}
            value={frequencyLabel ?? config.weeklyFrequency ?? "—"}
          />
          {isStimulationCategory(category) && (
            <>
              <Item
                label={t("DEVICE_INTENSITY_MA")}
                value={config.intensityMa !== undefined ? `${config.intensityMa} mA` : "—"}
              />
              <Item
                label={t("DEVICE_SHAM")}
                value={config.sham ? t("DEVICE_SHAM_ON") : t("DEVICE_SHAM_OFF")}
              />
              {supportsFrequency(category) && (
                <Item
                  label={t("DEVICE_FREQUENCY_HZ")}
                  value={config.frequencyHz ? `${config.frequencyHz} Hz` : "—"}
                />
              )}
            </>
          )}
        </div>
      </section>

      {category === "nf" && (config.nfElectrodes?.length ?? 0) > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("DEVICE_NF_PROTOCOL_SUMMARY")}</div>
          <div className={styles.rowList}>
            {config.nfElectrodes!.map((electrode) => (
              <div className={styles.row} key={electrode.site}>
                <span className={styles.rowKey}>{electrode.site}</span>
                <span className={styles.rowValue}>{describeWaves(electrode)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {category === "loreta" && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("LORETA_SELECTIONS")}</div>
          <div className={styles.grid} style={{ marginBottom: 10 }}>
            <Item
              label={t("LORETA_MODE")}
              value={config.loretaMode ? LORETA_MODE_DISPLAY[config.loretaMode] : "—"}
            />
          </div>
          <div className={styles.chipRow}>
            {(config.loretaSelections ?? []).map((selection) => (
              <span className={styles.chip} key={selection} dir="ltr">
                {selection}
              </span>
            ))}
          </div>
        </section>
      )}

      {isStimulationCategory(category) && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("DEVICE_ELECTRODES_POLARITY")}</div>
          <div className={styles.rowList}>
            {Object.entries(config.electrodes ?? {}).map(([site, polarity]) => (
              <div className={styles.row} key={site}>
                <span className={styles.rowKey}>{site}</span>
                <span className={styles.rowValue}>
                  {t(polarity === "anode" ? "DEVICE_ANODE" : "DEVICE_CATHODE")}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {config.notes && (
        <section className={styles.section}>
          <div className={styles.sectionTitle}>{t("DEVICE_NOTES")}</div>
          <p className={styles.notes}>{config.notes}</p>
        </section>
      )}
    </>
  );
}

function describeWaves(electrode: NfElectrode): string {
  const active = getActiveNfWaves(electrode);
  if (active.length === 0) return "—";

  return active
    .map((wave) => {
      const band =
        wave.band === "custom"
          ? wave.customRange || t("DEVICE_WAVE_CUSTOM")
          : t(NF_WAVE_BANDS.find((b) => b.key === wave.band)?.labelKey ?? wave.band);
      const mode = NF_WAVE_MODES.find((m) => m.value === wave.mode);
      return `${band} · ${mode ? t(mode.labelKey) : wave.mode}`;
    })
    .join(", ");
}

interface DeviceConfigDetailsProps {
  device: AppointmentDevice;
  onClose: () => void;
}

/**
 * Standalone details view for the device attached to an appointment, so a technician can open the
 * full montage straight from the calendar instead of reconstructing it from the one-line summary.
 */
function DeviceConfigDetails({ device, onClose }: DeviceConfigDetailsProps) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h2 className={styles.title}>{device.deviceName || t("DEVICE_SESSION")}</h2>
        {device.category && (
          <span className={styles.kindBadge}>
            {DEVICE_CATEGORY_DISPLAY[device.category] ?? device.category}
          </span>
        )}
      </div>

      <DeviceConfigSections config={device} />

      <div className={styles.footer}>
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          {t("APPOINTMENTS_CLOSE")}
        </button>
      </div>
    </div>
  );
}

export default DeviceConfigDetails;
